import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { playCelebrationTadaSfx } from '../audio/quizSfx';
import './LevelUpCelebrationModal.css';

const levelUpConfettiColors = ['#39ff14', '#00f2fe', '#ba0cf8', '#4facfe', '#ffe066'];

function fireLevelUpConfetti() {
  confetti({
    particleCount: 100,
    spread: 72,
    origin: { y: 0.55 },
    colors: levelUpConfettiColors,
    disableForReducedMotion: true,
  });
  setTimeout(() => {
    confetti({
      particleCount: 40,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors: levelUpConfettiColors,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors: levelUpConfettiColors,
      disableForReducedMotion: true,
    });
  }, 200);
}

export interface LevelUpCelebrationModalProps {
  show: boolean;
  oldLevel: number;
  newLevel: number;
  onClose: () => void;
}

const WING_SVG = (
  <svg viewBox="0 0 52 72" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M8 68 C4 48 6 28 20 12 L26 4 L32 14 C44 30 48 50 44 68 Z"
      fill="url(#wingGrad)"
      stroke="rgba(0,242,254,0.5)"
      strokeWidth="1"
    />
    <path
      d="M26 20 L30 36 L42 40 L30 44 L26 58 L22 44 L10 40 L22 36 Z"
      fill="rgba(186,12,248,0.35)"
    />
    <defs>
      <linearGradient id="wingGrad" x1="8" y1="4" x2="44" y2="68" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00f2fe" stopOpacity="0.5" />
        <stop offset="1" stopColor="#ba0cf8" stopOpacity="0.35" />
      </linearGradient>
    </defs>
  </svg>
);

const HEX_SVG = (
  <svg viewBox="0 0 108 124" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <polygon
      points="54,4 100,30 100,82 54,108 8,82 8,30"
      fill="url(#hexFill)"
      stroke="url(#hexStroke)"
      strokeWidth="2"
    />
    <defs>
      <linearGradient id="hexFill" x1="8" y1="4" x2="100" y2="108" gradientUnits="userSpaceOnUse">
        <stop stopColor="#1e293b" />
        <stop offset="1" stopColor="#0f172a" />
      </linearGradient>
      <linearGradient id="hexStroke" x1="8" y1="4" x2="100" y2="108" gradientUnits="userSpaceOnUse">
        <stop stopColor="#00f2fe" />
        <stop offset="1" stopColor="#ba0cf8" />
      </linearGradient>
    </defs>
  </svg>
);

const LevelUpCelebrationModal: React.FC<LevelUpCelebrationModalProps> = ({
  show,
  oldLevel,
  newLevel,
  onClose,
}) => {
  const { t } = useSafeTranslation();
  const continueBtnRef = useRef<HTMLButtonElement>(null);
  const [xpFillFull, setXpFillFull] = useState(false);

  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!show) return;
    const raf = requestAnimationFrame(() => {
      playCelebrationTadaSfx();
      if (!reduceMotion) {
        fireLevelUpConfetti();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [show, reduceMotion]);

  useEffect(() => {
    if (!show) {
      setXpFillFull(false);
      return;
    }
    if (reduceMotion) {
      setXpFillFull(true);
      return;
    }
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setXpFillFull(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [show, reduceMotion]);

  useEffect(() => {
    if (show && continueBtnRef.current) {
      continueBtnRef.current.focus();
    }
  }, [show]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  };

  const handleContinueKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClose();
    }
  };

  const continueLabel = t('achievements.celebrationContinue', 'Continue');

  if (!show) return null;

  const contentClass = reduceMotion
    ? 'level-up-modal-content'
    : 'level-up-modal-content level-up-modal-content--neon-pulse';
  const titleClass = reduceMotion ? 'level-up-title' : 'level-up-title level-up-title--pulse';

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard
      role="dialog"
      className="level-up-celebration-modal"
      backdropClassName="level-up-celebration-backdrop"
      dialogClassName="level-up-celebration-dialog"
      contentClassName="border-0 bg-transparent shadow-none"
      onKeyDown={handleKeyDown}
      aria-labelledby="level-up-dialog-title"
    >
      <Modal.Body className="p-0" onKeyDown={handleKeyDown}>
        <div className={contentClass}>
          <header className="level-up-header">
            <h2 id="level-up-dialog-title" className={titleClass}>
              {t('levelUp.title', 'LEVEL UP!')}
            </h2>
            <p className="level-up-subtitle">
              {t('levelUp.newLevel', { defaultValue: 'NEW LEVEL: {{level}}', level: newLevel })}
            </p>
          </header>

          <div className="level-up-badge-wrap" aria-hidden="true">
            <div className="level-up-buff-arrows">
              <span className="buff-arrow">▲</span>
              <span className="buff-arrow">▲</span>
              <span className="buff-arrow">▲</span>
            </div>
            <div className="level-up-wing level-up-wing--left">{WING_SVG}</div>
            <div className="level-up-hex-badge">
              {HEX_SVG}
              <span className="level-up-hex-level">{newLevel}</span>
            </div>
            <div className="level-up-wing level-up-wing--right">{WING_SVG}</div>
          </div>

          <section className="level-up-xp-section" aria-label={t('levelUp.experienceGained', 'EXPERIENCE GAINED:')}>
            <span className="level-up-xp-label">{t('levelUp.experienceGained', 'EXPERIENCE GAINED:')}</span>
            <div className="level-up-xp-track">
              <span className="level-up-xp-level-mark">{oldLevel}</span>
              <div className="level-up-xp-bar-outer">
                <div
                  className={`level-up-xp-fill${xpFillFull ? ' level-up-xp-fill--full' : ''}`}
                />
              </div>
              <span className="level-up-xp-level-mark">{newLevel}</span>
            </div>
          </section>

          <button
            ref={continueBtnRef}
            type="button"
            className="level-up-continue-btn"
            onClick={handleClose}
            onKeyDown={handleContinueKeyDown}
          >
            {continueLabel}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LevelUpCelebrationModal;
